<template>
    <div>
      <h1 class="text-center my-2">Mission Blocks</h1>
      <add-or-update-mission-block
        :is-add-or-update-active.sync="isAddOrUpdateActive"
        :update-data="updateData"
        @refetchData="fetchAllData"
        @resetUpdateData="resetUpdateData"
      />

      <list-table
        :records="records"
        :columns="tableColumns"
        :total-records="totalRecords"
        :search-filter.sync="search"
        :current-page-number.sync="currentPage"
        :show-search="true"
        :show-filter-by-default="true"
      >
        <template v-if="can('mission_blocks:CanAddNew')" #action_button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            @click="isAddOrUpdateActive = true"
          >
            <span class="align-middle text-lg">Add Mission Block</span>
          </b-button>
        </template>

        <template #cell(rmcs)="data">
          <b-link @click="goToRmcs(data.item)">
            <b-badge pill variant="light-primary" class="text-capitalize">
              {{ (data.item.rmcs || []).length }}
            </b-badge>
          </b-link>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item v-if="can('mission_blocks:CanUpdate')" @click="onEdit(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item v-if="can('mission_blocks:CanDelete')" @click="onDelete(data.item._id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </list-table>
    </div>
  </template>

  <script>
  import Ripple from 'vue-ripple-directive'
  import ListTable from "@core/components/ListTable/ListTable.vue";
  import AddOrUpdateMissionBlock from "@/@core/components/shared/mission-blocks/AddOrUpdateMissionBlockForm.vue"
  import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

  import { debounce, get } from "lodash";
  import { formatDate } from "@core/utils/utils";
  import { BLink, BButton, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";

  const watchHandler = {
    handler(){
      this.debounceFetchAllData()
    },
    immediate: false
  }

  const watchHandlerRequiresPageReset = {
    handler(){
      this.currentPage = 1;
      this.debounceFetchAllData()
    },
    immediate: false
  }

  export default {
    components: {
      BLink,
      BBadge,
      BButton,
      ListTable,
      BDropdown,
      BDropdownItem,
      AddOrUpdateMissionBlock,
    },
    directives: {
      Ripple,
    },
    data() {
      return {
        loading: false,
        records: [],

        search: "",
        updateData: null,
        debounceFetchAllData: null,
        isAddOrUpdateActive: false,

        tableColumns: [
          { key: 'title', label: "Mission Block", sortable: false },
          { key: 'description', sortable: false },
          { key: 'rmcs', label: "RMCs", sortable: false },
          { key: 'created', sortable: false, formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`, label: 'Date', class: 'width-200' },
          { key: 'actions' },
        ]
      }
    },
    computed: {
      perPage: {
        get() {
          return this.$store.getters[`navigation/recordsPerPage`];
        },
        set(value){
          this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
        }
      },
      currentPage: {
        get() {
          return this.$store.getters[`navigation/currentPage`];
        },
        set(value){
          this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
        }
      },
      totalRecords: {
        get(){
          return this.$store.getters[`navigation/totalRecords`];
        },
        set(value){
          this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
        }
      },
    },
    watch: {
      currentPage: watchHandler,
      search: watchHandlerRequiresPageReset,
      perPage: watchHandlerRequiresPageReset,
    },
    created() {
      this.debounceFetchAllData = debounce(this.fetchAllData, 500);

      const {
        search,
        page = 1,
        limit = this.perPage
      } = this.$route.query;

      this.search = search;
      this.perPage = +limit;
      this.currentPage = +page;
    },
    methods: {
      async fetchAllData() {
        try {
          this.loading = true;

          const query = {
            limit: this.perPage,
            search: this.search,
            page: this.currentPage,
          }
          await this.$router.push({ query }).catch(() => {});

          const request = await this.useJwt().sharedService.fetchMissionBlocks(query);
          const { data, pagination } = request.data;

          this.totalRecords = pagination.totalRecords;
          this.perPage = pagination.limit;
          this.records = data;
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.loading = false;
        }
      },
      async onDelete(id){
        try {
          this.loading = true;

          const result = await this.$swal({
            title: 'Confirm Delete?',
            text: "Do you want to delete this record?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, proceed.',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          });

          if (!result.value) {
            return;
          }

          await this.useJwt().sharedService.deleteMissionBlock(id)

          await this.fetchAllData()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'Record deleted successfully'
            },
          });
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message

          this.$nextTick(() => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error!',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error_message
              },
            });
          })
        } finally {
          this.loading = false;
        }
      },
      onEdit(update){
        this.updateData = update;
        this.isAddOrUpdateActive = true;
      },
      resetUpdateData(){
        if (this.updateData){
          this.updateData = null
        }
      },
      goToRmcs(missionBlock){
        this.$router.push({ name: 'rmcs', query: { block_id: missionBlock._id } }).catch(() => {});
      },
    }
  }
  </script>
